// Dev-only internal validation panel. Renders ONLY on localhost / lovable
// preview/dev hosts (same gate as ConnectedDevicesPanel). No end-user surface.
// Measures the existing recovery-only outlook against the user's own history.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  computeOutlookAccuracy,
  type OutlookAccuracyResult,
} from "./symptomRiskForecast";

const PL = "#4B1F4F";
const GR = "#6E4B71";
const TL = "#FAF1F4";

export function OutlookAccuracyPanel() {
  const isSandboxHost =
    typeof window !== "undefined" &&
    /(localhost|127\.0\.0\.1|lovable\.app|lovable\.dev)/i.test(window.location.hostname);

  const [result, setResult] = useState<OutlookAccuracyResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(90);

  async function run() {
    setBusy(true); setError(null);
    try {
      const { data } = await supabase.auth.getUser();
      const uid = data.user?.id;
      if (!uid) { setError("Not signed in"); return; }
      const r = await computeOutlookAccuracy(uid, days);
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { if (isSandboxHost) void run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (!isSandboxHost) return null;

  const fmt = (v: number | null, d = 2) => (v == null ? "—" : v.toFixed(d));

  return (
    <div style={{ background: WhiteCard, border: `1px solid ${TL}`, borderRadius: 14, padding: 16, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: PL }}>Outlook validation (dev only)</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ fontSize: 12, padding: "4px 6px", border: `1px solid ${TL}`, borderRadius: 6, color: PL }}
          >
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
          </select>
          <button
            onClick={() => void run()}
            disabled={busy}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "none", background: PL, color: "white", cursor: busy ? "default" : "pointer" }}
          >
            {busy ? "Running…" : "Recompute"}
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: GR, marginBottom: 10 }}>
        Internal-only honest measurement of the existing recovery-only heuristic
        against this user's logged symptom severity. Never shown to end users.
      </div>
      {error && <div style={{ fontSize: 12, color: "#a33" }}>Error: {error}</div>}
      {result && (
        <div style={{ fontSize: 12, color: PL, lineHeight: 1.6 }}>
          <div><strong>Status:</strong> {result.status} · <strong>Paired days:</strong> {result.pairedDays} / {result.windowDays}</div>
          {result.status === "insufficient_data" ? (
            <div style={{ color: GR, marginTop: 4 }}>
              Not enough paired data yet (need ≥30 paired days and all three bands populated).
            </div>
          ) : (
            <>
              <div style={{ marginTop: 6 }}>
                <strong>Mean severity by band</strong> (expect Lower &lt; Moderate &lt; Elevated):
                <ul style={{ margin: "4px 0 0 18px" }}>
                  <li>Lower: n={result.byBand.Lower.n}, mean={fmt(result.byBand.Lower.meanSeverity)}</li>
                  <li>Moderate: n={result.byBand.Moderate.n}, mean={fmt(result.byBand.Moderate.meanSeverity)}</li>
                  <li>Elevated: n={result.byBand.Elevated.n}, mean={fmt(result.byBand.Elevated.meanSeverity)}</li>
                </ul>
              </div>
              <div style={{ marginTop: 6 }}>
                <strong>Spearman ρ</strong> (score vs daily mean severity): {fmt(result.spearman, 3)}<br/>
                <strong>AUC</strong> (predicting top-tertile severity day): {fmt(result.auc, 3)}
              </div>
            </>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: GR }}>
            Diagnostics — wearable rows fetched: {result.notes.wearableRowsFetched},
            severity days fetched: {result.notes.severityDaysFetched},
            skipped (cold-start baseline): {result.notes.skippedColdStart}
          </div>
        </div>
      )}
    </div>
  );
}

const WhiteCard = "#ffffff";
